import {
  CustomOrderAdminDto,
  CustomOrderClientDto,
  CustomOrderPaymentAdminDto,
  ExternalStoredPaymentMethodAdminDto,
  ExternalStoredPaymentMethodClientDto,
  OrderAdminDto,
  OrderClientDto,
  OrderPaymentAdminDto,
  OrderPaymentClientDto,
  PaymentTransactionAdminDto,
  PaymentTransactionClientDto,
  StoredPaymentMethodAdminDto,
  StoredPaymentMethodClientDto,
} from 'service-api/src/types/api'
import { i18WithParams as t } from './locales'
import { capitalizeFirstLetter } from './common'

export type OrderPayment = OrderPaymentClientDto | OrderPaymentAdminDto
export type PaymentTransaction = PaymentTransactionClientDto | PaymentTransactionAdminDto
export type StoredPaymentMethod = StoredPaymentMethodAdminDto | StoredPaymentMethodClientDto
export type ExternalStoredPaymentMethod = ExternalStoredPaymentMethodAdminDto | ExternalStoredPaymentMethodClientDto

export const adyenPaymentType = (defaultLabel: string, paymentMethod?: StoredPaymentMethod) => {
  const { lastFour, brand } = paymentMethod ?? {}
  const baseLabel = brand && brand !== 'UNKNOWN' ? capitalizeFirstLetter(brand) : defaultLabel
  return lastFour ? t('global.label.cardEndingIn', { label: baseLabel, lastFour }) : baseLabel
}

export const paymentType = (payFacType?: OrderPayment['payFacType'], paymentMethod?: StoredPaymentMethod) => {
  if (payFacType === 'ADYEN_CARD_PRESENT') return adyenPaymentType(t('global.label.cardTerminal'), paymentMethod)
  else if (payFacType === 'ADYEN') return adyenPaymentType(t('global.label.card'), paymentMethod)
  else if (payFacType === 'TILLED') return t('global.label.card')
  else if (payFacType === 'CASH' || payFacType === 'OFFLINE') return t('global.label.cash')
  else if (payFacType === 'OTHER') return t('global.label.other')
  else if (payFacType === 'VOUCHER_CASH') return t('global.label.wallet')
  return ''
}

export const getInvoiceDepositTxns = (payments?: OrderPayment[]): Map<string, PaymentTransaction[]> => {
  if (!payments) return new Map()
  const orderTransactions = payments.flatMap(
    (payment) =>
      (isDeposit(payment) && payment.transactions?.map((transaction) => ({ ...transaction, orderPayment: payment }))) ||
      []
  )
  const invoiceTxns = new Map<string, PaymentTransaction[]>()

  orderTransactions.forEach((txn) => {
    if (!isCountableTransaction(txn)) {
      return
    }

    const invoiceTxn = invoiceTxns.get(txn.invoiceId!)

    if (invoiceTxn) {
      invoiceTxn.push(txn)
    } else {
      invoiceTxns.set(txn.invoiceId!, [txn])
    }
  })

  return invoiceTxns
}

export const isDeposit = (payment: OrderPayment) => payment.depositDue !== null && payment.depositDue !== undefined

export const getDepositDue = (order: OrderClientDto | OrderAdminDto | undefined) => {
  return (
    (order?.payments || []).reduce(
      (acc, payment) => acc + (payment.step === 'PENDING' ? (payment.depositDue ?? 0) : 0),
      0
    ) ?? 0
  )
}

export const adyenPaymentToUseFromPendingOrder = (
  order: CustomOrderClientDto | CustomOrderAdminDto | undefined
): CustomOrderPaymentAdminDto | undefined => {
  return order?.payments?.find((p) => p.step === 'PENDING' && p.payFacType === 'ADYEN')
}

export const isCountableTransaction = (transaction: PaymentTransaction) => {
  return !['CANCELED', 'FAILED'].includes(transaction.paymentStatus || '')
}

export const getInvoiceRefundTotal = (invoiceId?: string, payments?: OrderPayment[]) => {
  if (!invoiceId || !payments?.length) return 0
  const invoiceTxns = getInvoiceDepositTxns(payments).get(invoiceId)
  if (!invoiceTxns?.length) return 0
  return invoiceTxns.reduce((total, txn) => total + (txn.type === 'REFUND' ? txn.amount || 0 : 0), 0)
}

export const totalInvoiceDeposit = (invoiceId?: string, payments?: OrderPayment[]) => {
  if (!invoiceId || !payments?.length) return 0
  const invoiceTxns = getInvoiceDepositTxns(payments).get(invoiceId)
  if (!invoiceTxns?.length) return 0

  const uniquePayments = new Set<string>()
  return invoiceTxns.reduce((total, txn) => {
    const paymentId = txn.orderPayment?.id
    if (paymentId && !uniquePayments.has(paymentId)) {
      uniquePayments.add(paymentId)
      return total + (txn.orderPayment?.depositDue || 0)
    }
    return total
  }, 0)
}

export const calculateInvoicePayFacTotals = (transactions?: PaymentTransaction[], refundTotalsOnly?: boolean) => {
  const txnMap = new Map<OrderPayment['payFacType'], number>()

  if (!transactions?.length) return Array.from(txnMap)

  const getAmount = (transaction: PaymentTransaction) => {
    const amount = transaction.amount || 0
    if (refundTotalsOnly) {
      return transaction.type === 'REFUND' ? amount : 0
    }
    return transaction.type === 'CHARGE' ? amount : -amount
  }

  transactions.forEach((txn) => {
    if (!isCountableTransaction(txn)) {
      return
    }

    const payFacType = txn.orderPayment?.payFacType
    const currentAmount = txnMap.get(payFacType) || 0
    txnMap.set(payFacType, currentAmount + getAmount(txn))
  })

  return Array.from(txnMap)
}

export const generateIdempotencyKey = () =>
  [...crypto.getRandomValues(new Uint8Array(32))].map((b) => b.toString(16).padStart(2, '0')).join('')
